import { Grid, Tab, Tabs } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { usePaymentSchedules } from "../../services/agreementsService";
import { useAgreementDrafts, useEmailDrafts, useOfferDrafts, useNotificationDrafts } from "../../services/draftsService";
import { AgreementsTab } from "./AgreementsTab";
import { EmailTemplatesTab } from "./EmailTemplatesTab";
import { OffersTab } from "./OffersTab";
import { PaymentSchedulesTab } from "./PaymentSchedulesTab";
import { NotificationTemplatesTab } from "./NotificatinTemplatesTab";

const ACTIVE_TAB_STORAGE_KEY = "salesDraftsActiveTab";

export function SalesDraftsPage(props) {
    const { t } = useTranslation();

    const [activeTab, setActiveTab] = useState(() => {
        const savedTab = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
        return savedTab ? Number.parseInt(savedTab, 10) : 0;
    });

    const [dataLocal, setDataLocal] = useState([]);
    const [notificationDataLocal, setNotificationDataLocal] = useState([]);
    const [offersDataLocal, setOffersDataLocal] = useState([]);
    const [agreementsDataLocal, setAgreementsDataLocal] = useState([]);
    const [paymentSchedulesDataLocal, setPaymentSchedulesDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState({});
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState({});
    const [recordsCount, setRecordsCount] = useState(0);
    const [notificationsRecordsCount, setNotificationsRecordsCount] = useState(0);
    const [offersRecordsCount, setOffersRecordsCount] = useState(0);
    const [agreementsRecordsCount, setAgreementsRecordsCount] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [checkedAll, setCheckedAll] = useState(false);

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();


    // search params for offers, emails, agreements, payments are not used
    const { isLoading: isLoadingOffers, data: offerDraftsResponse } = useOfferDrafts();
    const { data: emailDraftsResponse, isLoading: isLoadingEmails } = useEmailDrafts();
    const { data: notificationDraftsResponse, isLoading: isLoadingNotifications } = useNotificationDrafts(searchParams);
    const { data: agreementDraftsResponse, isLoading: isLoadingAgreements } =
        useAgreementDrafts();
    const { data: paymentSchedulesResponse, isLoading: isLoadingSchedules } =
        usePaymentSchedules();

    useEffect(() => {
        if (emailDraftsResponse) {
            setDataLocal(
                emailDraftsResponse.results.map((draft) => ({
                    id: draft.id,
                    template_number: draft.nr,
                    template_name: draft.name,
                    description: draft.description,
                    subject: draft.subject || "",
                    body: draft.body || "",
                    active: draft.active,
                    creation_date: draft.created,
                    language: draft.language,
                })),
            );
            setRecordsCount(emailDraftsResponse.count);
        }
        if (notificationDraftsResponse) {
            setNotificationDataLocal(
                notificationDraftsResponse.results.map((draft) => ({
                    id: draft.id,
                    template_number: draft.nr,
                    template_name: draft.name,
                    description: draft.description,
                    subject: draft.subject || "",
                    body: draft.body || "",
                    active: draft.active,
                    creation_date: draft.created,
                    language: draft.language,
                    draft_notification: draft.draft_notification,
                    draft_notification_id: draft.draft_notification_id
                })),
            );
            setNotificationsRecordsCount(notificationDraftsResponse.count);
        }
        if (offerDraftsResponse) {
            setOffersDataLocal(
                offerDraftsResponse.results.map((draft) => ({
                    id: draft.id,
                    template_number: draft.nr,
                    template_name: draft.name,
                    investment: { id: 0, investment_name_sales: "-" },
                    description: draft.description,
                    file_name: draft.file_name,
                    active: draft.active,
                    creation_date: draft.created,
                    language: draft.language,
                })),
            );
            setOffersRecordsCount(offerDraftsResponse.count);
        }
        if (agreementDraftsResponse) {
            setAgreementsDataLocal(
                agreementDraftsResponse.results.map((draft) => ({
                    id: draft.id,
                    template_number: draft.nr,
                    template_name: draft.name,
                    investment: { id: 0, investment_name_sales: "-" },
                    description: draft.description,
                    file_name: draft.file_name,
                    active: draft.active,
                    creation_date: draft.created,
                    language: draft.language,
                })),
            );
            setAgreementsRecordsCount(agreementDraftsResponse.count);
        }

        if (paymentSchedulesResponse) {
            setPaymentSchedulesDataLocal(paymentSchedulesResponse);
        }
    }, [
        emailDraftsResponse,
        offerDraftsResponse,
        agreementDraftsResponse,
        paymentSchedulesResponse,
        notificationDraftsResponse,
    ]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // Save to localStorage
        localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, newValue.toString());
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 0:
                return (
                    <OffersTab
                        offersDataLocal={offersDataLocal}
                        setOffersDataLocal={setOffersDataLocal}
                        offersRecordsCount={offersRecordsCount}
                        resetPageNumber={resetPageNumber}
                        selectedRowId={selectedRowId}
                        setSelectedRowId={setSelectedRowId}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        checkedAll={checkedAll}
                        setCheckedAll={setCheckedAll}
                        dataPage={dataPage}
                        handleChangeDataPage={handleChangeDataPage}
                        dataPageSize={dataPageSize}
                        handleChangeDataPageSize={handleChangeDataPageSize}
                        searchParams={searchParams}
                    />
                );
            case 1:
                return (
                    <EmailTemplatesTab
                        dataLocal={dataLocal}
                        setDataLocal={setDataLocal}
                        recordsCount={recordsCount}
                        resetPageNumber={resetPageNumber}
                        selectedRowId={selectedRowId}
                        setSelectedRowId={setSelectedRowId}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        checkedAll={checkedAll}
                        setCheckedAll={setCheckedAll}
                        dataPage={dataPage}
                        handleChangeDataPage={handleChangeDataPage}
                        dataPageSize={dataPageSize}
                        handleChangeDataPageSize={handleChangeDataPageSize}
                        searchParams={searchParams}
                    />
                );
            case 2:
                return (
                    <AgreementsTab
                        draftsDataLocal={agreementsDataLocal}
                        setDraftsDataLocal={setAgreementsDataLocal}
                        recordCount={agreementsRecordsCount}
                        resetPageNumber={resetPageNumber}
                        selectedRowId={selectedRowId}
                        setSelectedRowId={setSelectedRowId}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        checkedAll={checkedAll}
                        setCheckedAll={setCheckedAll}
                        dataPage={dataPage}
                        handleChangeDataPage={handleChangeDataPage}
                        dataPageSize={dataPageSize}
                        handleChangeDataPageSize={handleChangeDataPageSize}
                        searchParams={searchParams}
                    />
                );
            case 3:
                return (
                    <PaymentSchedulesTab
                        dataLocal={paymentSchedulesDataLocal}
                        setDataLocal={setPaymentSchedulesDataLocal}
                    />
                );
            case 4:
                return (
                    <NotificationTemplatesTab
                        dataLocal={notificationDataLocal}
                        setDataLocal={setNotificationDataLocal}
                        recordsCount={notificationsRecordsCount}
                        resetPageNumber={resetPageNumber}
                        selectedRowId={selectedRowId}
                        setSelectedRowId={setSelectedRowId}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        checkedAll={checkedAll}
                        setCheckedAll={setCheckedAll}
                        dataPage={dataPage}
                        handleChangeDataPage={handleChangeDataPage}
                        dataPageSize={dataPageSize}
                        handleChangeDataPageSize={handleChangeDataPageSize}
                        searchParams={searchParams}
                        setSearchParams={setSearchParams}
                    />
                );
            default:
                return null;
        }
    };

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("drafts.title").toUpperCase()}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={12}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        aria-label="drafts tabs"
                        variant="fullWidth"
                        sx={{
                            "& .MuiTab-root": {
                                minWidth: "auto",
                                flex: 1,
                                fontSize: "1rem",
                                fontWeight: 500,
                            },
                        }}
                    >
                        <Tab label={t("drafts.tab.offers")} />
                        <Tab label={t("drafts.tab.emails")} />
                        <Tab label={t("drafts.tab.agreements")} />
                        <Tab label={t("drafts.tab.paymentSchedules")} />
                        <Tab label={t("drafts.tab.notifications")} />
                    </Tabs>
                </Grid>

                <Grid item xs={12} sx={{ mt: 1 }}>
                    <LoaderWrapper
                        showLoader={
                            isLoadingEmails ||
                            isLoadingOffers ||
                            isLoadingAgreements ||
                            isLoadingSchedules ||
                            isLoadingNotifications
                        }
                    >
                        {renderTabContent()}
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
